/* eslint-disable no-restricted-globals */
import React, { useEffect, useState, lazy } from "react";
import "./App.css";
import "antd/dist/antd.css";
import { ThunderboltOutlined } from "@ant-design/icons";
import axios from "axios";
import { ThemeProvider } from "styled-components";
import { useDarkMode } from "./hooks/useDarkMode";
import { GlobalStyles } from "./styles/GlobalStyles";
import { lightTheme, darkTheme } from "./themes/Themes";
import Spinner from "./components/Spinner/Spinner";
import Toggle from "./components/Toggler/Toggler";
import { Globals } from "react-spring";
import { useReduceMotion } from "react-reduce-motion";
import { Popconfirm } from "antd";
import ComponentLoader from "./ComponentLoader";
import CookieConsent from "react-cookie-consent";
import AboutLogo from "./assets/about.jpg";
import { MenuIcon } from "./components/MenuIcon/MenuIcon";
import { m, LazyMotion } from "framer-motion";
import CacheBuster from "./CacheBuster";
import ToggleSwitch from "./components/ToggleSwitch/ToggleSwitch";

const Mail = lazy(() => ComponentLoader(() => import("./container/Mail/Mail"), 3));
const GridGallery = lazy(() => ComponentLoader(() => import("./container/GridGallery"), 3));
const Introtext = lazy(() => ComponentLoader(() => import("./container/IntroText/IntroText"), 3));
const Landing = lazy(() => ComponentLoader(() => import("./container/Landing/Landing"), 3));
const Menu = lazy(() => ComponentLoader(() => import("./container/Menu/Menu"), 3));
const Swiper = lazy(() => ComponentLoader(() => import("./container/Swiper/Swiper"), 3));
const Flip = lazy(() => ComponentLoader(() => import("./container/Flip/Flip"), 3));
const Carousel = lazy(() => ComponentLoader(() => import("./container/Carousel/Carousel"), 3));

const galleryGridHeights = [100, 200];
const masonryGridHeights = [150, 200, 300, 400];

function App() {
  const [flipped, setFlip] = useState(false);
  const [gallery, setGallery] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [status, setStatus] = useState({
    loadedGallery: false,
    loadedMasonry: false,
    loadedReels: false,
  });
  const [gridData, setGridData] = useState(false);
  const [masonryData, setMasonryData] = useState(false);
  const [reels, setReels] = useState(false);

  const prefersReducedMotion = useReduceMotion();
  React.useEffect(() => {
    Globals.assign({
      skipAnimation: prefersReducedMotion,
    });
  }, [prefersReducedMotion]);

  // Make sure to return the specific export containing the feature bundle.
  const loadFeatures = () => import("./features.js").then((res) => res.default);

  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [skipWaiting, setSkipWaiting] = useState(false);
  const [right, setRight] = useState("10vw");

  const showPopconfirm = () => {
    setVisible(true);
  };

  const handleOk = () => {
    setConfirmLoading(true);
    const customEvent = new CustomEvent("update-notification-click", {
      detail: {
        action: "update-app",
        callback: () => {
          setVisible(false);
          setConfirmLoading(false);
        },
      },
    });
    skipWaiting && skipWaiting();
    self.dispatchEvent(customEvent);
  };

  const handleCancel = () => {
    console.log("Clicked cancel button");
    const customEvent = new CustomEvent("update-notification-click", {
      detail: {
        action: "not-update-app",
        callback: () => {
          setVisible(false);
        },
      },
    });
    self.dispatchEvent(new Event("onCancelUpdate"));
    self.dispatchEvent(customEvent);
  };

  useEffect(() => {
    const resizeEventHandler = window.addEventListener("resize", () => setRight("10vw"));
    const interval = setInterval(() => {
      setRight("10vw");
    }, 2500);
    return () => {
      window.removeEventListener("resize", resizeEventHandler);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const listenPopShowEvent = self.addEventListener("show-update-popup", ({ detail }) => {
      setSkipWaiting(detail.onConfirm);
      console.log("show popup");
      showPopconfirm();
    });

    return () => self.removeEventListener("show-update-popup", listenPopShowEvent);
  }, []);

  const [theme, themeToggler, mountedComponent] = useDarkMode();

  // const themeMode = theme === "light" ? lightTheme : darkTheme;

  const [themeMode, setThemeMode] = useState(theme === "light" ? lightTheme : darkTheme);

  useEffect(() => {
    setThemeMode(theme === "light" ? lightTheme : darkTheme);
  }, [theme]);

  function handleCheckboxChange(checked) {
    setGallery(checked);
    setTimeout(() => document.getElementById("gallery-container").scrollIntoView(), 750);
  }

  function setMuted(muted) {
    setIsMuted(muted);
  }

  useEffect(() => {
    document.addEventListener("scroll", () => {
      onScroll();
    });
    function onScroll() {
      const menuIcon = document.getElementById("menu");
      const header = document.getElementById("header");
      const intro = document.getElementById("intro");
      const bodyScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      if (bodyScrollTop > header?.offsetHeight) {
        menuIcon?.classList.remove("menu-hidden");
        menuIcon?.classList.add("menu");
      } else {
        menuIcon?.classList.remove("menu");
        menuIcon?.classList.add("menu-hidden");
      }
      if (bodyScrollTop > intro?.offsetHeight) {
        setMuted(true);
      } else {
        setMuted(false);
      }
    }
    return () => {
      document.removeEventListener("scroll", () => {
        onScroll();
      });
    };
  }, []);

  useEffect(() => {
    async function fetchImages() {
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/media/images`, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          const data = res.data?.urls?.map((url) => ({
            css: `url(${url})`,
            height: galleryGridHeights[Math.floor(Math.random() * galleryGridHeights.length)],
            name: url,
            description: "",
          }));
          setGridData(data);
          setStatus((status) => ({ ...status, loadedGallery: true }));
        })
        .catch((err) => {
          console.log(err);
        });
    }
    fetchImages();

    async function fetchMasonryImages() {
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/media/masonry-images`, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          const data = res.data?.urls?.map((url) => ({
            css: `url(${url})`,
            height: masonryGridHeights[Math.floor(Math.random() * masonryGridHeights.length)],
            name: url,
            description: "",
          }));
          setMasonryData(data);
          setStatus((status) => ({ ...status, loadedMasonry: true }));
        })
        .catch((err) => {
          console.log(err);
        });
    }
    fetchMasonryImages();

    async function fetchReels() {
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/media/reels`, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          setReels(res.data.urls);
          setStatus((status) => ({ ...status, loadedReels: true }));
        })
        .catch((err) => {
          console.log(err);
        });
    }
    fetchReels();
  }, []);

  const winboxComponent = document.querySelector("#show-winbox");

  useEffect(() => {
    mountedComponent &&
      winboxComponent &&
      window.dispatchEvent(new CustomEvent("initializeWinbox"));
  }, [mountedComponent, winboxComponent]);

  if (!mountedComponent) return <Spinner />;

  return (
    <ThemeProvider theme={themeMode}>
      <LazyMotion features={loadFeatures} strict>
        <GlobalStyles />
        <div className="App">
          <Popconfirm
            placement="topRight"
            title="New version of our app is available!"
            visible={visible}
            onConfirm={handleOk}
            okButtonProps={{
              loading: confirmLoading,
            }}
            onCancel={handleCancel}
            okText="Update"
            cancelText="Not now"
            icon={<ThunderboltOutlined style={{ color: "blue" }} />}
            okType="primary"
          >
            <div id="header" className="home-header">
              <Toggle theme={theme} toggleTheme={themeToggler} />
              <div id="menu" className="menu-hidden">
                <Menu isDarkTheme={theme === "dark"} />
              </div>
              <h1 className="title">18 Megapixels</h1>
              <h2 className="sub-title">PHOTOGRAPHY</h2>
            </div>
          </Popconfirm>

          <CacheBuster>
            {({ loading, isLatestVersion, refreshCacheAndReload }) => {
              if (loading) return null;
              if (!loading && !isLatestVersion) {
                // You can decide how and when you want to force reload
                refreshCacheAndReload();
              }
              return (
                <>
                  <div id="intro">
                    {/* <Introtext /> */}
                    {status.loadedReels && (
                      <>
                        <h1>Reels</h1>
                        <Swiper isMuted={isMuted} status={status.loadedReels} data={reels} />
                      </>
                    )}
                  </div>
                  <div className="masonary-grid" id="collection">
                    {status.loadedMasonry && (
                      <>
                        <h1>Recents</h1>
                        <Landing
                          data={masonryData}
                          status={status.loadedMasonry}
                          masonryGridHeights={masonryGridHeights}
                        />
                      </>
                    )}
                  </div>
                  <div id="gallery-container">
                    <div className="gallery-header">
                      <h1 className="gallery-text" id="gallery">
                        Collection
                      </h1>
                      <div className="slider-container">
                        <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                          <m.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="gesture-switch"
                          >
                            <ToggleSwitch
                              optionLabels={["G", "S"]}
                              checked={gallery}
                              onChange={handleCheckboxChange}
                              id={"gallery-switch"}
                              name={"Gallery Views"}
                            />
                          </m.button>
                        </div>
                        <span>
                          <em>{`Switch to ${gallery ? "Grid" : "SlideShow"}!`}</em>
                        </span>
                      </div>
                    </div>
                    {!gallery ? (
                      <GridGallery data={gridData} status={status.loadedGallery} />
                    ) : (
                      <Carousel data={gridData} status={status.loadedGallery} />
                    )}
                  </div>
                  <div id="about" className="about-container">
                    <h1>Behind the Lens</h1>
                    <div
                      className="about-logo-container"
                      id="show-winbox"
                      title="Explore about me"
                      style={{ right }}
                    >
                      {!flipped && (
                        <MenuIcon noRotate={true} scaleMax={true} loopToInfinity={true}>
                          <img src={AboutLogo} alt="About Us" className="about-logo" />
                        </MenuIcon>
                      )}
                    </div>
                    <Flip flipped={flipped} setFlip={setFlip}>
                      <Introtext />
                      <Mail flipped={flipped} />
                    </Flip>
                  </div>
                </>
              );
            }}
          </CacheBuster>
        </div>
      </LazyMotion>
      <CookieConsent
        location="bottom"
        buttonText="Yes, I understand!"
        cookieName="eighteen-megapixels-v0"
        style={{ background: "#2B373B" }}
        buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
        expires={150}
        hideOnAccept={true}
        sameSite="strict"
        // debug={true} for development purpose
      >
        This website uses cookies to enhance the user experience.{" "}
        <span style={{ fontSize: "10px" }}>From D2KDevelopers</span>
      </CookieConsent>
    </ThemeProvider>
  );
}

export default App;
